/**
 * Hook to check approval settings and user permissions
 * 
 * Returns:
 *   approvalSettings: { jobPostings: boolean, offerLetters: boolean }
 *   userPermissions: { canPostJobs, canPublishJobs, canDraftOffers, canApproveOffers, canApprove }
 *   loading: boolean
 *   isApprover: boolean (convenience flag)
 *   requiresJobApproval: boolean (convenience flag - settings ON + user lacks permission)
 *   requiresOfferApproval: boolean
 */
import { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import useTeamAccessProfile from './useTeamAccessProfile';
import {
    OWNER_PERMISSIONS,
    DEFAULT_COLLABORATOR_PERMISSIONS,
    normalizePermissionState
} from '../utils/teamAccess';

export const useApprovalSettings = (companyUID) => {
    const teamAccess = useTeamAccessProfile(companyUID);
    const [approvalSettings, setApprovalSettings] = useState({
        jobPostings: false,
        offerLetters: false
    });

    useEffect(() => {
        if (!companyUID) return undefined;

        let settingsUnsubscribe;

        const fetchData = async () => {
            try {
                const settingsRef = ref(database, `companies/${companyUID}/settings/approvals`);
                settingsUnsubscribe = onValue(settingsRef, (snapshot) => {
                    if (snapshot.exists()) {
                        const data = snapshot.val();
                        setApprovalSettings({
                            jobPostings: data.jobPostings === true,
                            offerLetters: data.offerLetters === true
                        });
                    } else {
                        // Default: approvals OFF
                        setApprovalSettings({
                            jobPostings: false,
                            offerLetters: false
                        });
                    }
                });
            } catch (error) {
                console.error('Error fetching approval settings:', error);
            }
        };

        fetchData();

        // Cleanup
        return () => {
            if (settingsUnsubscribe) settingsUnsubscribe();
        };
    }, [companyUID]);

    const userPermissions = normalizePermissionState(
        teamAccess.isCompanyOwner
            ? OWNER_PERMISSIONS
            : (teamAccess.userPermissions || DEFAULT_COLLABORATOR_PERMISSIONS)
    );
    const loading = teamAccess.loading;
    const isCompanyOwner = teamAccess.isCompanyOwner;

    const isJobApprover = userPermissions.canApprove === true || userPermissions.canReviewJobs === true;
    const isOfferApprover = userPermissions.canApprove === true || userPermissions.canReviewOffers === true;
    const isApprover = userPermissions.canApprove === true || userPermissions.canReviewJobs === true || userPermissions.canReviewOffers === true;
    const requiresJobApproval = approvalSettings.jobPostings && !userPermissions.canPublishJobs;
    const requiresOfferApproval = approvalSettings.offerLetters && !userPermissions.canApproveOffers;

    return {
        approvalSettings,
        userPermissions,
        loading,
        isCompanyOwner,
        isApprover,
        isJobApprover,
        isOfferApprover,
        requiresJobApproval,
        requiresOfferApproval
    };
};

export default useApprovalSettings;

// Export constants for use elsewhere
export { OWNER_PERMISSIONS, DEFAULT_COLLABORATOR_PERMISSIONS };
